import { useState, useEffect } from 'react';
import {
  ShoppingCart,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  Package,
} from 'lucide-react';
import {
  credits,
  CreditTransaction,
  CreditProductEntry,
} from '../../api/client';
import { formatNumber, cn } from '../../lib/utils';

const PAGE_SIZE = 50;

// Known product IDs and their display names
const PRODUCT_NAMES: Record<string, string> = {
  'com.koyaventures.personallm.credits_1000': 'Starter Pack',
  'com.koyaventures.personallm.credits_2500': 'Plus Pack',
  'com.koyaventures.personallm.credits_6500': 'Premium Pack',
  'com.koyaventures.personallm.credits_14000': 'Ultra Pack',
};

export default function CreditsPurchases() {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<CreditProductEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedTx, setExpandedTx] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [purchaseRes, productRes] = await Promise.all([
        credits.purchases({ limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
        credits.products(),
      ]);
      setTransactions(purchaseRes.transactions);
      setTotal(purchaseRes.total);
      setProducts(productRes.products);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Compute summary stats
  const totalCredits = transactions.reduce(
    (sum, tx) => sum + (tx.type === 'purchase' ? Math.abs(tx.amount) : 0),
    0
  );
  const refundCount = transactions.filter(
    (tx) => tx.type === 'refund'
  ).length;

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading purchases...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Purchases</h1>
          <p className="text-muted-foreground mt-1">
            {formatNumber(total)} purchase transactions
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Product Catalog */}
      {products.length > 0 && (
        <div className="animate-fade-in-up animate-delay-100">
          <h2 className="text-lg font-semibold mb-4">Product Catalog</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="card-glow rounded-2xl p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-teal-400" />
                  </div>
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded text-[10px] font-medium uppercase',
                      product.active
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-rose-500/15 text-rose-400'
                    )}
                  >
                    {product.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm font-medium mb-1">
                  {PRODUCT_NAMES[product.product_id] || product.product_id}
                </p>
                <p className="text-2xl font-bold text-teal-400">
                  {formatNumber(product.credits)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">credits</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up animate-delay-200">
        <div className="card-glow rounded-2xl p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Purchases on Page
          </p>
          <p className="text-2xl font-bold">
            {transactions.filter((tx) => tx.type === 'purchase').length}
          </p>
        </div>
        <div className="card-glow rounded-2xl p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Credits Purchased
          </p>
          <p className="text-2xl font-bold text-emerald-400">
            {formatNumber(totalCredits)}
          </p>
        </div>
        <div className="card-glow rounded-2xl p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Refunds on Page
          </p>
          <p className={cn('text-2xl font-bold', refundCount > 0 ? 'text-rose-400' : '')}>
            {refundCount}
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-300">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-8" />
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                ID
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Device
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Credits
              </th>
              <th className="px-4 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Balance After
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Env
              </th>
              <th className="px-4 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Time
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => (
              <PurchaseRow
                key={tx.id}
                tx={tx}
                isExpanded={expandedTx === tx.id}
                onToggle={() =>
                  setExpandedTx(expandedTx === tx.id ? null : tx.id)
                }
              />
            ))}
            {transactions.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <ShoppingCart className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">
                      No purchases yet
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between animate-fade-in-up animate-delay-300">
          <p className="text-sm text-muted-foreground">
            Showing {page * PAGE_SIZE + 1}–
            {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:text-foreground'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page >= totalPages - 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:text-foreground'
              )}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PurchaseRow({
  tx,
  isExpanded,
  onToggle,
}: {
  tx: CreditTransaction;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const typeStyles: Record<string, string> = {
    purchase: 'bg-teal-500/15 text-teal-400',
    refund: 'bg-rose-500/15 text-rose-400',
  };

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-emerald-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </td>
        <td className="px-4 py-3">
          <code className="text-xs text-muted-foreground font-mono">
            {tx.id.slice(0, 8)}
          </code>
        </td>
        <td className="px-4 py-3">
          <code className="text-xs text-muted-foreground font-mono">
            {tx.device_id.slice(0, 8)}...{tx.device_id.slice(-4)}
          </code>
        </td>
        <td className="px-4 py-3">
          <span
            className={cn(
              'px-2 py-0.5 rounded text-[10px] font-medium uppercase',
              typeStyles[tx.type] || 'bg-secondary text-muted-foreground'
            )}
          >
            {tx.type}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          <span
            className={cn(
              'font-mono text-sm font-medium',
              tx.amount > 0 ? 'text-emerald-400' : 'text-rose-400'
            )}
          >
            {tx.amount > 0 ? '+' : ''}
            {formatNumber(Math.abs(tx.amount))}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          <span className="font-mono text-sm text-muted-foreground">
            {formatNumber(tx.balance_after)}
          </span>
        </td>
        <td className="px-4 py-3">
          {tx.purchase_environment ? (
            <span
              className={cn(
                'px-2 py-0.5 rounded text-[10px] font-medium uppercase',
                tx.purchase_environment === 'sandbox'
                  ? 'bg-amber-500/15 text-amber-400'
                  : 'bg-emerald-500/15 text-emerald-400'
              )}
            >
              {tx.purchase_environment}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/50">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {new Date(tx.created_at).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} className="px-6 py-0">
            <div className="py-4 pl-8 border-l-2 border-teal-500/30 ml-2 grid grid-cols-2 md:grid-cols-4 gap-4">
              <DetailItem label="Full ID" value={tx.id} mono />
              <DetailItem label="Device" value={tx.device_id} mono />
              <DetailItem
                label="Credits"
                value={`${tx.amount > 0 ? '+' : ''}${formatNumber(Math.abs(tx.amount))}`}
              />
              <DetailItem
                label="Balance After"
                value={formatNumber(tx.balance_after)}
              />
              <DetailItem label="Reason" value={tx.reason || '—'} />
              <DetailItem label="Environment" value={tx.purchase_environment || '—'} />
              <DetailItem label="Apple Txn ID" value={tx.purchase_transaction_id || '—'} mono />
              <DetailItem label="Note" value={tx.admin_note || '—'} />
              <DetailItem label="Job ID" value={tx.gen_job_id || '—'} mono />
              <DetailItem
                label="Created"
                value={new Date(tx.created_at).toLocaleString()}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <p className={cn('text-sm break-all', mono ? 'font-mono text-xs' : '')}>
        {value}
      </p>
    </div>
  );
}
